import logger from '../utils/logger';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Navbar } from '../components/layout/Navbar';
import { MobileNav } from '../components/layout/MobileNav';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { formatCAD, formatTransactionAmount } from '../utils/currency';
import { 
  Wallet, 
  ArrowUpCircle, 
  Send, 
  ArrowDownCircle, 
  ArrowUpRight,
  ArrowDownLeft,
  ChevronRight,
  TrendingUp,
  Shield,
  X
} from 'lucide-react';

export default function Dashboard() {
  const { t, language } = useLanguage();
  const { user, api, logout } = useAuth();
  const [balance, setBalance] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Check if admin is impersonating a user
  const isImpersonating = localStorage.getItem('payhub_impersonating') === 'true';
  const impersonatedUser = isImpersonating ? JSON.parse(localStorage.getItem('payhub_impersonated_user') || '{}') : null;

  // Function to exit impersonation and return to admin
  const exitImpersonation = async () => {
    // Clears the httpOnly session cookie server-side + local flags
    await logout();
    window.location.href = '/admin';
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [balanceRes, txRes] = await Promise.all([
          api.get('/wallet/balance'),
          api.get('/transactions/?limit=5')
        ]);
        setBalance(balanceRes.data);
        setTransactions(txRes.data.transactions || []);
      } catch (error) {
        logger.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [api]);

  const quickActions = [
    { 
      path: '/topup', 
      label: t('nav.topup'), 
      icon: ArrowUpCircle,
      color: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400'
    },
    { 
      path: '/transfer', 
      label: t('nav.transfer'), 
      icon: Send,
      color: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
    },
    { 
      path: '/withdraw', 
      label: t('nav.withdraw'), 
      icon: ArrowDownCircle,
      color: 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400'
    }
  ];

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'topup':
      case 'transfer_in':
      case 'refund':
        return <ArrowDownLeft className="h-4 w-4 text-green-600" />;
      case 'transfer_out':
      case 'withdraw':
      case 'payment':
        return <ArrowUpRight className="h-4 w-4 text-red-500" />;
      default:
        return <Wallet className="h-4 w-4" />;
    }
  };

  const getTransactionAmount = (tx) => {
    const isCredit = ['topup', 'transfer_in', 'refund'].includes(tx.type);
    return (
      <span className={`font-semibold ${isCredit ? 'text-green-600' : 'text-red-500'}`}>
        {formatTransactionAmount(tx.amount, isCredit)}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      <Navbar />
      
      {/* Impersonation Banner */}
      {isImpersonating && (
        <div className="bg-amber-500 text-white px-4 py-2 sticky top-0 z-50 shadow-md">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Shield className="h-5 w-5" />
              <span className="font-medium">
                {language === 'fr' 
                  ? `Mode Admin : Vous visualisez le compte de ${impersonatedUser?.name || user?.name}`
                  : `Admin Mode: Viewing account of ${impersonatedUser?.name || user?.name}`
                }
              </span>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={exitImpersonation}
              className="text-white hover:bg-amber-600 gap-2"
              data-testid="exit-impersonation-btn"
            >
              <X className="h-4 w-4" />
              {language === 'fr' ? 'Retour Admin' : 'Exit to Admin'}
            </Button>
          </div>
        </div>
      )}
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Welcome */}
        <div className="mb-6 animate-fade-in-up">
          <h1 className="text-2xl sm:text-3xl font-bold">
            {t('dashboard.welcome')}, {user?.name?.split(' ')[0]}!
          </h1>
        </div>

        {/* Balance Card */}
        <Card className="balance-gradient text-white mb-6 overflow-hidden animate-fade-in-up stagger-1" data-testid="balance-card">
          <CardContent className="p-6 sm:p-8 relative">
            <div className="absolute top-0 right-0 h-40 w-40 bg-white/10 rounded-full blur-3xl" />
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-12 w-12 rounded-xl bg-white/20 flex items-center justify-center">
                  <Wallet className="h-6 w-6" />
                </div>
                <div>
                  <p className="text-white/80 text-sm">{t('dashboard.balance')}</p>
                  {loading ? (
                    <Skeleton className="h-8 w-32 bg-white/20" />
                  ) : (
                    <p className="text-3xl sm:text-4xl font-bold">
                      {formatCAD(balance?.balance || 0)}
                      <span className="text-lg ml-1 text-white/80">{t('common.cad')}</span>
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 text-white/80 text-sm">
                <TrendingUp className="h-4 w-4" />
                <span>{t('dashboard.credits')} 1:1 CAD</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions - Visible for admin impersonation to act on behalf of user */}
        <div className="mb-6 animate-fade-in-up stagger-2">
          <h2 className="text-lg font-semibold mb-4">{t('dashboard.quick_actions')}</h2>
          <div className="grid grid-cols-3 gap-3 sm:gap-4">
            {quickActions.map(({ path, label, icon: Icon, color }) => (
              <Link key={path} to={path} data-testid={`quick-action-${path.slice(1)}`}>
                <Card className="hover:shadow-md transition-all duration-300 cursor-pointer h-full">
                  <CardContent className="p-4 sm:p-6 flex flex-col items-center text-center">
                    <div className={`h-12 w-12 rounded-xl ${color} flex items-center justify-center mb-3`}>
                      <Icon className="h-6 w-6" />
                    </div>
                    <span className="text-sm font-medium">{label}</span>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>

        {/* Recent Transactions */}
        <Card className="animate-fade-in-up stagger-3" data-testid="recent-transactions">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg">{t('dashboard.recent_transactions')}</CardTitle>
            <Link to="/transactions">
              <Button variant="ghost" size="sm" className="text-primary" data-testid="view-all-transactions">
                {t('dashboard.view_all')}
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex items-center justify-between py-3">
                    <div className="flex items-center gap-3">
                      <Skeleton className="h-10 w-10 rounded-full" />
                      <div>
                        <Skeleton className="h-4 w-32 mb-1" />
                        <Skeleton className="h-3 w-20" />
                      </div>
                    </div>
                    <Skeleton className="h-5 w-16" />
                  </div>
                ))}
              </div>
            ) : transactions.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Wallet className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>{t('dashboard.no_transactions')}</p>
              </div>
            ) : (
              <div className="space-y-1">
                {transactions.map((tx) => (
                  <div 
                    key={tx.transaction_id} 
                    className="flex items-center justify-between py-3 border-b border-border/50 last:border-0"
                    data-testid={`transaction-${tx.transaction_id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
                        {getTransactionIcon(tx.type)}
                      </div>
                      <div>
                        <p className="text-sm font-medium line-clamp-1">{tx.description}</p>
                        <p className="text-xs text-muted-foreground">{formatDate(tx.created_at)}</p>
                      </div>
                    </div>
                    {getTransactionAmount(tx)}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      <MobileNav />
    </div>
  );
}
